import { useState, useEffect, useRef, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

interface SignInProps {
  onLogin: () => void
}

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback'
const TURNSTILE_SITEKEY = '0x4AAAAAACDWBmAofRySJT2U'

// Promise to track script loading globally
let turnstileLoadPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  // If already loaded, resolve immediately
  if (window.turnstile) {
    return Promise.resolve()
  }

  // If already loading, return existing promise
  if (turnstileLoadPromise) {
    return turnstileLoadPromise
  }

  turnstileLoadPromise = new Promise((resolve, reject) => {
    // Set up the callback that Turnstile will call when ready
    window.onloadTurnstileCallback = () => {
      resolve()
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`)
    if (existingScript) {
      // Script exists but hasn't loaded yet, wait for our callback
      return
    }

    // Create and insert script
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true
    script.onerror = () => {
      turnstileLoadPromise = null
      reject(new Error('Failed to load Turnstile script'))
    }
    document.head.appendChild(script)
  })

  return turnstileLoadPromise
}

export default function SignIn({ onLogin }: SignInProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [error, setError] = useState('')
  const [turnstileLoading, setTurnstileLoading] = useState(true)
  const navigate = useNavigate()
  
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    let mounted = true

    const initializeTurnstile = async () => {
      try {
        await loadTurnstileScript()
        
        if (!mounted || !containerRef.current || !window.turnstile) {
          return
        }

        // Clean up any existing widget in this container
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current)
          } catch {
            // Ignore removal errors
          }
          widgetIdRef.current = null
        }

        // Render the widget
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITEKEY,
          theme: 'light',
          callback: (token: string) => {
            if (mounted) {
              setTurnstileToken(token)
            }
          },
          'error-callback': () => {
            if (mounted) {
              setError('Security verification failed. Please refresh the page.')
              setTurnstileLoading(false)
            }
          },
          'expired-callback': () => {
            if (mounted) {
              setTurnstileToken('')
            }
          },
        })

        if (mounted) {
          setTurnstileLoading(false)
        }
      } catch (err) {
        console.error('Turnstile initialization failed:', err)
        if (mounted) {
          setError('Failed to load security verification. Please refresh the page.')
          setTurnstileLoading(false)
        }
      }
    }

    initializeTurnstile()

    return () => {
      mounted = false
      // Clean up widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null
      }
    }
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!turnstileToken) {
      setError('Please complete the security check.')
      return
    }

    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, turnstileToken }),
      })

      const data = await res.json() as { success: boolean; error?: string }

      if (res.ok && data.success) {
        onLogin()
        navigate('/')
      } else {
        setError(data.error || 'Invalid credentials or security check failed.')
        // Reset Turnstile for retry
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current)
          setTurnstileToken('')
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
      console.error(err)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-6 shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Sign in to Gorkie Admin
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} method="POST">
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="sr-only">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="relative block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="relative block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-center">
            <div ref={containerRef} className="min-h-[65px]">
              {turnstileLoading && (
                <div className="flex items-center justify-center h-[65px] text-gray-500 text-sm">
                  Loading security check...
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="text-center text-sm text-red-600">{error}</div>
          )}

          <div>
            <button
              type="submit"
              className="group relative flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}